import React, { useState, useEffect } from 'react';
import { formatDateTime } from '../../utils/dateFormatter';
import StaffLayout from '../../components/StaffLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, Database, List, Clock, Activity, FileText, Repeat, CreditCard } from 'lucide-react';
import lmsApi from '../../services/api';
import { toast } from 'sonner';

const DataTableView = ({ endpoint, columns, title }: { endpoint: string, columns: { key: string, label: string }[], title: string }) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await lmsApi.post(`${endpoint}list_post/`, {});
            setData(response.data);
        } catch (error) {
            console.error(error);
            toast.error(`Failed to fetch ${title}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [endpoint]);

    const filteredData = data.filter(item => 
        Object.values(item).some(val => 
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                        placeholder={`Search ${title}...`} 
                        className="pl-10" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            <div className="rounded-md border bg-white overflow-hidden">
                <Table>
                    <TableHeader className="bg-gray-50">
                        <TableRow>
                            {columns.map(col => (
                                <TableHead key={col.key} className="font-bold text-gray-700 whitespace-nowrap">
                                    {col.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-32 text-center">
                                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                                    <span className="text-sm text-gray-500 mt-2 block">Loading data...</span>
                                </TableCell>
                            </TableRow>
                        ) : filteredData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-32 text-center text-gray-500">
                                    No records found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData.map((row, i) => (
                                <TableRow key={i} className="hover:bg-gray-50 transition-colors">
                                    {columns.map(col => (
                                        <TableCell key={col.key} className="text-sm py-3">
                                            {typeof row[col.key] === 'boolean' ? (
                                                <Badge variant={row[col.key] ? "default" : "secondary"}>
                                                    {row[col.key] ? 'True' : 'False'}
                                                </Badge>
                                            ) : col.key.includes('amt') || col.key.includes('amount') || col.key === 'pos' ? (
                                                `₹${Number(row[col.key]).toLocaleString()}`
                                            ) : col.key.includes('date') || col.key === 'timestamp' ? (
                                                formatDateTime(row[col.key])
                                            ) : (
                                                String(row[col.key] || '-')
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="text-[10px] text-gray-400 font-medium">
                Total Records: {filteredData.length}
            </div>
        </div>
    );
};

const StaffDataExplorer = () => {
    return (
        <StaffLayout title="LMS Data Explorer">
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">T* Tables Explorer</h2>
                    <p className="text-gray-500 text-sm">Real-time view of system state and historical logs.</p>
                </div>

                <Tabs defaultValue="account-status" className="w-full">
                    <TabsList className="bg-white border p-1 h-auto mb-6 flex-wrap justify-start">
                        <TabsTrigger value="account-status" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <Activity className="h-4 w-4" /> Account Status
                        </TabsTrigger>
                        <TabsTrigger value="dpd-status" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <Clock className="h-4 w-4" /> DPD Snapshots
                        </TabsTrigger>
                        <TabsTrigger value="bill-logs" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <List className="h-4 w-4" /> Bill Logs
                        </TabsTrigger>
                        <TabsTrigger value="demands" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <FileText className="h-4 w-4" /> Demands
                        </TabsTrigger>
                        <TabsTrigger value="transactions" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <CreditCard className="h-4 w-4" /> Transactions
                        </TabsTrigger>
                        <TabsTrigger value="amort" className="flex gap-2 items-center py-2 px-4 data-[state=active]:bg-primary data-[state=active]:text-white">
                            <Repeat className="h-4 w-4" /> Amort Schedule
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="account-status">
                        <DataTableView 
                            endpoint="/staff/account-status/" 
                            title="Account Status Logs"
                            columns={[
                                { key: 'loan', label: 'Loan ID' },
                                { key: 'status', label: 'Status' },
                                { key: 'status_date', label: 'Status Date' },
                                { key: 'timestamp', label: 'System Timestamp' },
                                { key: 'posted_by', label: 'By' }
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="dpd-status">
                        <DataTableView 
                            endpoint="/staff/dpd-status/" 
                            title="DPD Snapshots"
                            columns={[
                                { key: 'loan', label: 'Loan ID' },
                                { key: 'dpd', label: 'DPD' },
                                { key: 'pos', label: 'POS (₹)' },
                                { key: 'overdue_amount', label: 'Overdue Total' },
                                { key: 'overdue_amount_p', label: 'OD Principal' },
                                { key: 'overdue_amount_i', label: 'OD Interest' },
                                { key: 'snapshot_type', label: 'Type' },
                                { key: 'status_date', label: 'Status Date' }
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="bill-logs">
                        <DataTableView 
                            endpoint="/staff/bill-logs/" 
                            title="Bill Logs"
                            columns={[
                                { key: 'loan', label: 'Loan ID' },
                                { key: 'bill_id', label: 'Bill ID' },
                                { key: 'bill_type', label: 'Type' },
                                { key: 'bill_date', label: 'Date' },
                                { key: 'bill_amt', label: 'Bill Amt' },
                                { key: 'paid_amt', label: 'Paid Amt' },
                                { key: 'net_due', label: 'Net Due' },
                                { key: 'status', label: 'Status' }
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="demands">
                        <DataTableView 
                            endpoint="/staff/demands/" 
                            title="Collection Demands"
                            columns={[
                                { key: 'loan', label: 'Loan ID' },
                                { key: 'demand_id', label: 'Demand ID' },
                                { key: 'demand_date', label: 'Date' },
                                { key: 'demand_amt', label: 'Total Demand' },
                                { key: 'demand_status', label: 'Status' },
                                { key: 'principal_amt', label: 'Principal' },
                                { key: 'interest_amt', label: 'Interest' },
                                { key: 'fee_amt', label: 'Fees' }
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="transactions">
                        <DataTableView 
                            endpoint="/staff/transactions/" 
                            title="Transactions"
                            columns={[
                                { key: 'loan_application_number', label: 'Loan ID' },
                                { key: 'transaction_id', label: 'Txn ID' },
                                { key: 'transaction_date', label: 'Date' },
                                { key: 'amount', label: 'Amount' },
                                { key: 'transaction_type', label: 'Type' },
                                { key: 'bill_type', label: 'Category' },
                                { key: 'reference_id', label: 'Ref ID' }
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="amort">
                        <DataTableView 
                            endpoint="/staff/amort/" 
                            title="Amortization Schedule"
                            columns={[
                                { key: 'loan', label: 'Loan ID' },
                                { key: 'payment_date', label: 'Due Date' },
                                { key: 'payment_amount', label: 'Total EMI' },
                                { key: 'payment_amount_p', label: 'Principal' },
                                { key: 'payment_amount_i', label: 'Interest' },
                                { key: 'expected_principal_balance_after_payment', label: 'Exp POS' }
                            ]}
                        />
                    </TabsContent>
                </Tabs>
            </div>
        </StaffLayout>
    );
};

export default StaffDataExplorer;
